
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MegaphoneIcon, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdsDashboard } from '@/hooks/useAdsDashboard';
import ROUTES from '@/routes-map';
import { Badge } from '@/components/ui/badge';

interface AdStatsProps {
  adStats: {
    views: number;
    newPatients: number;
    spent: number;
  };
}

const DoctorAdStats: React.FC<AdStatsProps> = ({ adStats }) => {
  const { expiringAds, formatExpirationTime, renewAd } = useAdsDashboard();
  
  // Get the closest expiring ad
  const soonestExpiringAd = expiringAds.length > 0 
    ? expiringAds.reduce((closest, current) => {
        // Parse expiry dates to compare
        const closestExpiry = new Date(closest.expireDate);
        const currentExpiry = new Date(current.expireDate);
        return currentExpiry < closestExpiry ? current : closest;
      }, expiringAds[0]) 
    : null;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Ad Performance</CardTitle>
          <CardDescription>Quick overview of your ad campaigns</CardDescription>
        </div>
        <MegaphoneIcon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col items-center justify-center p-3 bg-muted/30 rounded-lg">
            <span className="text-sm text-muted-foreground">Views</span>
            <span className="text-2xl font-bold">{adStats.views}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 bg-muted/30 rounded-lg">
            <span className="text-sm text-muted-foreground">New Patients</span>
            <span className="text-2xl font-bold">{adStats.newPatients}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 bg-muted/30 rounded-lg">
            <span className="text-sm text-muted-foreground">Spent</span>
            <span className="text-2xl font-bold">${adStats.spent}</span>
          </div>
        </div>
        
        {soonestExpiringAd && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Active Ad Ends In: {formatExpirationTime(soonestExpiringAd)}
                </p>
                <p className="text-xs text-amber-700">
                  {soonestExpiringAd.type === 'featuredDoctor' 
                    ? 'Your Featured Doctor placement will end soon' 
                    : `Your ${soonestExpiringAd.type} promotion is about to expire`}
                </p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => renewAd(soonestExpiringAd.id)}
            >
              Renew
            </Button>
          </div>
        )}
        
        <div className="flex justify-end mt-4">
          <Button variant="outline" size="sm" asChild>
            <Link to={ROUTES.DOCTOR.ADS}>View Details</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DoctorAdStats;
